import { useMutation } from '@tanstack/react-query';
import { Formik, FormikHelpers } from 'formik';
import { useState } from 'react';

import { ContractPriceListStatus, PriceListActivationMode } from '../../../../types/ContractMessage';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { validatePriceLists } from '../../../common/api/clients/contracts.api';
import { uploadGroupFile } from '../../../common/api/clients/fileManagement.api';
import { translate } from '../../../common/helpers/translate.helper';
import { Box } from '../../../common/ui/Box';
import { useContractPriceListCreate } from '../../common/hooks/useContractPriceListCreate';
import { PriceListEditForm } from '../../ContractForm/PriceListSection/PriceListEditForm';
import { PriceListUpload } from '../../ContractForm/PriceListSection/PriceListUpload';
import {
    mapPriceListValidationErrors,
    toPriceListRequestPayload,
} from '../../ContractForm/PriceListSection/priceListValidation.helpers';
import { PriceListFormValue } from '../../ContractForm/PriceListSection/types';

interface ContractAddPriceListsModalProps {
    activationMode?: PriceListActivationMode;
    contractId: string;
    customerGroupId?: string;
    hasAppliedPriceList: boolean;
    initialValidFrom?: string;
    isOpen: boolean;
    onDismiss: () => void;
}

interface AddPriceListFormValues {
    priceListActivationMode: PriceListActivationMode;
    priceLists: [PriceListFormValue];
}

const createEmptyPriceList = (initialValidFrom?: string): PriceListFormValue => ({
    fileId: '',
    fileName: '',
    validFrom: initialValidFrom ?? null,
    validUntil: null,
    activeOnApproval: false,
    initialStatus: ContractPriceListStatus.PROCESSING,
    status: ContractPriceListStatus.PROCESSING,
});

const initialValues = (
    activationMode?: PriceListActivationMode,
    initialValidFrom?: string
): AddPriceListFormValues => ({
    priceListActivationMode: activationMode ?? PriceListActivationMode.AUTO,
    priceLists: [createEmptyPriceList(initialValidFrom)],
});

export const ContractAddPriceListsModal = ({
    activationMode,
    contractId,
    customerGroupId,
    hasAppliedPriceList,
    initialValidFrom,
    isOpen,
    onDismiss,
}: ContractAddPriceListsModalProps) => {
    const createPriceListsMutation = useContractPriceListCreate();
    const { addToast, addErrorToast } = useToasts();
    const [uploadProgress, setUploadProgress] = useState(0);

    const fileUploadMutation = useMutation({
        mutationFn: (file: File) =>
            uploadGroupFile(file, customerGroupId ?? '', (event) => {
                if (event.progress) {
                    setUploadProgress(event.progress * 100);
                }
            }),
    });

    const handleSubmit = async (
        values: AddPriceListFormValues,
        { setErrors }: FormikHelpers<AddPriceListFormValues>
    ) => {
        const validationResponse = await validatePriceLists(
            toPriceListRequestPayload([values.priceLists[0]], values.priceListActivationMode),
            contractId
        );

        if (validationResponse.errors.length > 0) {
            setErrors(mapPriceListValidationErrors<AddPriceListFormValues>(validationResponse, 0));
            return;
        }

        try {
            await createPriceListsMutation.mutateAsync({
                contractId,
                priceLists: toPriceListRequestPayload([values.priceLists[0]], values.priceListActivationMode),
            });
            addToast(translate('alert.uploadsuccess.message'));
            onDismiss();
        } catch {
            addErrorToast(translate('assignments.toasts.uploadError'));
        }
    };

    return (
        <Formik
            initialValues={initialValues(activationMode, initialValidFrom)}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {({ setFieldValue, submitForm, values }) => {
                const priceList = values.priceLists[0];
                const hasUploadedFile = !!priceList.fileId;

                const handleResetPriceList = () => {
                    void setFieldValue('priceLists[0]', createEmptyPriceList(initialValidFrom), false);
                    setUploadProgress(0);
                    fileUploadMutation.reset();
                };

                const handleFileSelected = (file: File) => {
                    if (!customerGroupId) {
                        return;
                    }

                    fileUploadMutation.mutate(file, {
                        onSuccess: (response) => {
                            setUploadProgress(0);
                            void setFieldValue(
                                'priceLists[0]',
                                {
                                    ...createEmptyPriceList(initialValidFrom),
                                    fileId: response.id,
                                    fileName: response.fileName,
                                },
                                false
                            );
                        },
                    });
                };

                return (
                    <Modal isOpen={isOpen} onDismiss={onDismiss} size="large">
                        <Box padding={24} display="grid" rowGap={24}>
                            <Box display="grid" rowGap={16}>
                                <Text variant="title">{translate('contracts.priceLists.addPriceList')}</Text>
                                <Text variant="small" color="foreground.muted">
                                    {translate('contracts.priceLists.description')}
                                </Text>

                                {hasUploadedFile ? (
                                    <Box
                                        padding={16}
                                        borderRadius={8}
                                        border="1px solid"
                                        borderColor="border.subtle"
                                        display="flex"
                                        flexDirection="column"
                                    >
                                        <PriceListEditForm
                                            activationMode={values.priceListActivationMode}
                                            disableActiveOnApproval={
                                                values.priceListActivationMode === PriceListActivationMode.MANUAL &&
                                                hasAppliedPriceList
                                            }
                                            activeOnApprovalDescription={
                                                values.priceListActivationMode === PriceListActivationMode.MANUAL &&
                                                hasAppliedPriceList
                                                    ? translate(
                                                          'contracts.priceLists.activeOnApproval.disabledDescription'
                                                      )
                                                    : undefined
                                            }
                                            index={0}
                                            fileName={priceList.fileName}
                                            onSave={() => submitForm()}
                                            onCancel={handleResetPriceList}
                                            onDeleteFile={handleResetPriceList}
                                        />
                                    </Box>
                                ) : (
                                    <PriceListUpload
                                        onFileSelected={handleFileSelected}
                                        uploadProgress={fileUploadMutation.isPending ? uploadProgress : undefined}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Modal>
                );
            }}
        </Formik>
    );
};
